// Release notes (#159): what's new for players, newest day first, from
// `src/release-notes.json`. Every merged change a player can see adds a
// bullet to its day there in the same PR; `items` names the backlog items a
// bullet covers and `quiet` the day's items with nothing to tell players
// (docs, refactors), so `release-notes.test.ts` can check the newest Done
// entry in BACKLOG.md made it in. Neither is shown.
//
// Pause > What's new lists them, and a dot marks the menu entry (and the
// start screen's link) until the newest day has been seen; the date last
// seen is kept in localStorage.

import data from "../release-notes.json";
import { HILL_SITE } from "./site";
import { storage } from "./storage";

export interface ReleaseNote {
  text: string;
  items?: readonly number[];
}

export interface ReleaseDay {
  /** YYYY-MM-DD. */
  date: string;
  notes: readonly ReleaseNote[];
  quiet?: readonly number[];
}

/** None on the hill site (#219): the notes are mostly about the parks, so it has no What's new. */
export const RELEASE_NOTES: readonly ReleaseDay[] = HILL_SITE ? [] : data.days;

/** The newest day with notes, or null with none. */
export const LATEST_NOTES: string | null = RELEASE_NOTES[0]?.date ?? null;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A day's heading: "Oct 3, 2026". Read from the string, so no time zone can move it a day. */
export function dayHeading(date: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** Whether there are notes newer than the day last seen (null: never looked). */
export function hasNewNotes(seen: string | null, latest: string | null = LATEST_NOTES): boolean {
  return latest !== null && (seen === null || seen < latest);
}

const KEY = "kickflip.notesSeen";

/** The newest day the player has seen, or null. */
export function loadNotesSeen(): string | null {
  try {
    return storage()?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
}

/** Remembers that the player has seen everything up to `date`. */
export function saveNotesSeen(date: string | null = LATEST_NOTES): void {
  if (date === null) return;
  try {
    storage()?.setItem(KEY, date);
  } catch {
    // Not allowed to save here: the dot comes back next visit.
  }
}
